import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import {
  PaletteProps,
  SpacingProps,
  TypographyProps,
  typography as typographyStyles,
  palette as paletteStyles,
} from '@material-ui/system';

import Memo from '../Memo';
import Box from '../Box';

// Make list of SpacingProp keys we want to Pick and make a set out of it for faster check since text gets used a lot.
// Then get type of BOX_PROP_KEYS  so we can create a string literal union of the keys to create the BoxProps type with
const BOX_PROP_KEYS = [
  'margin',
  'marginTop',
  'marginLeft',
  'marginBottom',
  'marginRight',
  'padding',
  'paddingTop',
  'paddingLeft',
  'paddingBottom',
  'paddingRight',
] as const;
const BOX_PROP_KEYS_SET = new Set([...BOX_PROP_KEYS] as string[]);
type BoxPropsTuple = typeof BOX_PROP_KEYS;
type BoxPropKeys = BoxPropsTuple[number];
type BoxProps = Pick<SpacingProps, BoxPropKeys>;

// All the different text tags (and div) we want to support
type VariantType = 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'li'
  | 'dt'
  | 'dd'
  | 'span'
  | 'div';

type BaseTextProps = {
  as?: VariantType,
  underlined?: boolean,
} & TypographyProps & Pick<PaletteProps, 'color'>;

export type TextProps = BaseTextProps & BoxProps;

// Used to extract just a BoxProps object from a TextProps object
// If all values for all keys in BoxProps object are null/undefined, return null so we can skip adding the Box
const extractBoxProps = (props: TextProps): BoxProps => {
  const boxProps = Object.keys(props)
    .filter(key => BOX_PROP_KEYS_SET.has(key))
    .reduce((obj, key) => {
        const typedKey = key as BoxPropKeys;
        obj[typedKey] = props[typedKey];
        return obj;
  }, {} as BoxProps);
  const boxPropsExist = Object.values(boxProps).some((value) => value);
  return boxPropsExist ? boxProps : null;
};

const calculateFontSize = (fontSize: string): string => {
  if (!fontSize) {
    return null;
  }
  return `${fontSize}`.includes('px') ? rem(fontSize) : fontSize;
};

const BaseText = styled('p').withConfig({
  // Prevent styles from being passed to html element directly
  shouldForwardProp: (prop, validator) => !['color', 'fontSize'].includes(prop) && validator(prop),
})<BaseTextProps>`
  margin: 0;
  text-decoration: ${(props) => (props.underlined ? 'underline' : null)};
  overflow-wrap: break-word;
  ${typographyStyles}
  ${paletteStyles}
`;

const Text: React.FC<PropsWithChildren<TextProps>> = (props: PropsWithChildren<TextProps>) => {
  const {
    children,
    as,
    color,
    fontSize,
    fontStyle,
    fontFamily,
    fontWeight,
    lineHeight,
    textAlign,
    letterSpacing,
    underlined,
  } = props;

  const size = calculateFontSize(fontSize);
  const styledText = (
    <BaseText
      as={as}
      color={color || '#202625'}
      fontSize={size}
      fontStyle={fontStyle}
      fontFamily={fontFamily}
      fontWeight={fontWeight}
      lineHeight={lineHeight || '1.5'}
      textAlign={textAlign}
      letterSpacing={letterSpacing}
      underlined={underlined}
    >
      {children}
    </BaseText>
  );
  const boxProps = extractBoxProps(props);
  if (boxProps) {
    const {
      margin,
      marginTop,
      marginLeft,
      marginBottom,
      marginRight,
      padding,
      paddingTop,
      paddingLeft,
      paddingBottom,
      paddingRight,
    } = boxProps;
    return (
      // Padding and Margin should not be added directly to text, but instead should be added to a container
      <Box
        display="inline-block"
        margin={margin}
        marginTop={marginTop}
        marginLeft={marginLeft}
        marginBottom={marginBottom}
        marginRight={marginRight}
        padding={padding}
        paddingTop={paddingTop}
        paddingLeft={paddingLeft}
        paddingBottom={paddingBottom}
        paddingRight={paddingRight}
      >
        {styledText}
      </Box>
    );
  }
  return styledText;
};

export default Memo(Text);
